import React, { useState, useEffect, useRef } from 'react';
import TaskColumn from './TaskColumn';
import { DragDropContext } from '@hello-pangea/dnd';
import { useOutletContext } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import axios from 'axios';
import progress2 from '../../image/progress2.gif'; // Assurez-vous que le chemin est correct
import { motion } from "framer-motion";
import logo from "../../image/progress1.gif";
import api from '../../api';


// Composant Modal de Confirmation
const ConfirmationModal = ({ isOpen, onConfirm, onCancel, title, message, confirmText, cancelText, type = 'warning' }) => {
  if (!isOpen) return null;

  const getIconAndColors = () => {
    switch (type) {
      case 'success':
        return {
          icon: '✓',
          bgColor: 'bg-green-50',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          confirmBg: 'bg-green-600 hover:bg-green-700'
        };
      case 'danger':
        return {
          icon: '⚠',
          bgColor: 'bg-red-50',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          confirmBg: 'bg-red-600 hover:bg-red-700'
        };
      case 'info':
        return {
          icon: 'ℹ',
          bgColor: 'bg-blue-50',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          confirmBg: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'move':
        return {
          icon: '↗',
          bgColor: 'bg-purple-50',
          iconBg: 'bg-purple-100',
          iconColor: 'text-purple-600',
          confirmBg: 'bg-purple-600 hover:bg-purple-700'
        };
      default: // warning
        return {
          icon: '⚠',
          bgColor: 'bg-amber-50',
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
          confirmBg: 'bg-amber-600 hover:bg-amber-700'
        };
    }
  };

  const { icon, bgColor, iconBg, iconColor, confirmBg } = getIconAndColors();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className={`mx-auto flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0 sm:h-12 sm:w-12`}>
            <span className={`text-2xl font-bold ${iconColor} sm:text-xl`}>
              {icon}
            </span>
          </div>

          <div className="mt-4 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 className="text-xl font-semibold leading-6 text-gray-900 sm:text-lg">
              {title}
            </h3>
            <div className="mt-3">
              <p className="text-sm text-gray-600 leading-relaxed">
                {message}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors sm:mt-0 sm:w-auto"
              onClick={onCancel}
            >
              {cancelText || 'Annuler'}
            </button>
            <button
              type="button"
              className={`inline-flex w-full justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors sm:w-auto ${confirmBg}`}
              onClick={onConfirm}
            >
              {confirmText || 'Confirmer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TaskBoard = () => {
  const {
    searchTerm,
    dateStart,
    dateEnd,
    priorityFilter,
    typeFilter,
    moduleFilter,         // ← récupéré ici
    organisationFilter,
    deadlineTechFilter,
    deadlineClientFilter
  } = useOutletContext();

  const [allStages, setAllStages] = useState([]);
  const [permittedStages, setPermStages] = useState([]);
  const [tasks, setTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingUpdates, setPendingUpdates] = useState(new Set()); // Tickets en cours de mise à jour
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const intervalRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [causeText, setCauseText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


  // État pour la modal de confirmation
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  const [utilisateur_id, setUtilisateur_id] = useState(null);
  const [module_id, setModule_id] = useState(null);
  const [testeur_id, setTesteur_id] = useState(null);
  const [roleId, setRoleId] = useState(null);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


    useEffect(() => {
      const fetchUser = async () => {
        try {
          const userResponse = await api.get('/me', { withCredentials: true });
          if (!userResponse.data.success) {
            console.error('Impossible de récupérer les informations utilisateur.');
            return;
          }
  
          const user = userResponse.data.user;
          setUtilisateur_id(parseInt(user.id, 10));
          setModule_id(parseInt(user.module_id, 10));
          setTesteur_id(parseInt(user.id, 10));
          setRoleId(parseInt(user.role_id, 10));
        } catch (error) {
          console.error('Erreur lors de la récupération des informations utilisateur.');
        }
      };
  
      fetchUser();
    }, []);





  

  // Fonction pour fermer la modal de confirmation
  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      type: 'warning'
    });
  };

  // 1) Charger toutes les étapes
  useEffect(() => {
    api.get('/parametrages/etapes')
      .then(res => {
        const etapes = res.data.etapes || [];
        etapes.sort((a, b) => a.order - b.order);
        setAllStages(etapes);
      })
      .catch(err => console.error('GET /etapes error:', err));
  }, []);

  // 2) Étapes autorisées pour le rôle
  useEffect(() => {
    if (!roleId || allStages.length === 0) return;

    api.get(`/parametrages/roles/${roleId}`)
      .then(res => {
        const role = res.data.role || res.data;
        const allowedIds = Array.isArray(role.etapes) ? role.etapes.map(e => e.id) : [];
        const filtered = allStages
          .filter(stage => allowedIds.includes(stage.id))
          .sort((a, b) => a.order - b.order);
        setPermStages(filtered);
      })
      .catch(err => console.error('Erreur GET /roles/{id} :', err));
  }, [roleId, allStages]);

  // 3) Fonction de chargement des tickets
  const fetchTickets = async () => {
    if (permittedStages.length === 0) return;

    try {
      const res = await api.get(`/tickets/modules/${module_id}`);
      const grouped = {};

      (res.data.tickets || []).forEach(ticket => {
        if (pendingUpdates.has(ticket.id)) return;

        const validationAdminCl = String(ticket.validation_adminCl);

        let isValidDemande = false;
        if (ticket.type === 'demande' && ticket.demande) {
          const confirmationAdmin = String(ticket.demande.confirmation_admin);
          const accepter = String(ticket.demande.accepter);
          isValidDemande = (confirmationAdmin === '1' && accepter === '1' && validationAdminCl === '4');
        }

        const isValidBug = (ticket.type === 'bug' && validationAdminCl === '4');
        if (!isValidDemande && !isValidBug) return;

        let sid = ticket.etape_id != null ? Number(ticket.etape_id) : permittedStages[0].id;
        if (!permittedStages.some(s => s.id === sid)) {
          sid = permittedStages[0].id;
        }

        grouped[sid] = grouped[sid] || [];
        grouped[sid].push({
          id: ticket.id,
          title: ticket.nom,
          content: ticket.content,
          type: ticket.type,
          deadlineClient: ticket.deadline_client,
          deadlineTech: ticket.deadline_tech,
          priorite: ticket.priorite,
          status: ticket.status,
          createur: ticket.createur,
          etape: sid,
          organisation: ticket.organisation,
          developpeurs: ticket.developpeurs || [],
          affectee_par: ticket.affectee_par,
          testeur: ticket.testeur,
          module: ticket.module_id,
        });
      });

      setTasks(prevTasks => {
        const newTasks = { ...grouped };
        pendingUpdates.forEach(ticketId => {
          Object.keys(prevTasks).forEach(stageId => {
            const ticket = prevTasks[stageId]?.find(t => t.id === ticketId);
            if (ticket) {
              newTasks[stageId] = newTasks[stageId] || [];
              if (!newTasks[stageId].find(t => t.id === ticketId)) {
                newTasks[stageId].push(ticket);
              }
            }
          });
        });
        return newTasks;
      });
    } catch (err) {
      console.error('GET /tickets error:', err);
    }
  };

  // 4) Charger et grouper les tickets
  useEffect(() => {
    if (permittedStages.length === 0) {
      setLoading(false); // ← S'assurer que loading est faux même s’il n’y a pas d’étapes
      return;
    }

    setLoading(true); // Lancer le chargement

    const loadTickets = async () => {
      await fetchTickets();
      setLoading(false);  // ← Arrêt du chargement après fetch
      setFirstLoadDone(true); // ← On note que le premier chargement est fait
    };

    loadTickets();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(fetchTickets, 3500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [permittedStages, utilisateur_id, testeur_id]);




  // Fonction pour identifier les étapes importantes nécessitant confirmation
  const needsConfirmation = (sourceStageId, destStageId) => {
    const sourceStage = allStages.find(s => s.id === sourceStageId);
    const destStage = allStages.find(s => s.id === destStageId);

    // Étapes importantes nécessitant confirmation (vous pouvez ajuster selon vos besoins)
    const criticalStages = ['LIVRÉE', 'TERMINÉ', 'VALIDÉ', 'DÉPLOYÉ', 'PRODUCTION'];

    return criticalStages.some(stage =>
      destStage?.nom?.toUpperCase().includes(stage) ||
      destStage?.id === 7 // ID spécifique pour "LIVRÉE"
    );
  };

  // Fonction pour obtenir le type de modal selon l'étape
  const getModalType = (destStageId) => {
    const destStage = allStages.find(s => s.id === destStageId);
    const stageName = destStage?.nom?.toUpperCase() || '';

    if (stageName.includes('LIVRÉE') || stageName.includes('TERMINÉ')) {
      return 'success';
    }
    if (stageName.includes('VALIDÉ') || stageName.includes('APPROUVÉ')) {
      return 'info';
    }
    if (stageName.includes('REJETÉ') || stageName.includes('ANNULÉ')) {
      return 'danger';
    }
    return 'move';
  };

  const handleConfirmReject = async () => {
    if (!pendingMove?.ticket || !pendingMove?.destStage) return;

    const { ticket, source, destination } = pendingMove;
    const rollback = () => {
      const rollbackSrcList = Array.from(tasks[destination.droppableId] || []);
      const rollbackDstList = Array.from(tasks[source.droppableId] || []);
      const [rollbackMoved] = rollbackSrcList.splice(destination.index, 1);
      rollbackMoved.etape = Number(source.droppableId);
      rollbackDstList.splice(source.index, 0, rollbackMoved);
      setTasks(prev => ({ ...prev, [source.droppableId]: rollbackDstList, [destination.droppableId]: rollbackSrcList }));
    };

    try {
      setIsProcessing(true);

      const updateData = {
        etape_id: 8,
        raison_retour: causeText.trim(),
        modifier: utilisateur_id
      };

      await api.post(`/tickets/${ticket.id}`, updateData);

      setShowReturnModal(false);
      setCauseText('');
      setPendingMove(null);
    } catch (error) {
      console.error("Erreur lors du retour :", error);
      rollback();
    } finally {
      setIsProcessing(false);
    }
  };


  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const src = Number(source.droppableId);
    const dst = Number(destination.droppableId);
    if (src === dst) return;

    const srcList = Array.from(tasks[src] || []);
    const moved = srcList[source.index];

    if (!moved) return;

    const sourceStage = allStages.find(s => s.id === src);
    const destStage = allStages.find(s => s.id === dst);
    const taskTitle = moved.title || `Ticket #${moved.id}`;
    if (dst === 8) {
      const srcList = Array.from(tasks[src] || []);
      const dstList = Array.from(tasks[dst] || []);
      const [movedTask] = srcList.splice(source.index, 1);
      movedTask.etape = dst;
      dstList.splice(destination.index, 0, movedTask);

      setTasks(prev => ({ ...prev, [src]: srcList, [dst]: dstList }));
      setShowReturnModal(true);
      setPendingMove({ ticket: movedTask, source, destination, destStage });
      return;
    }


    // Fonction pour effectuer le déplacement
    const performMove = async () => {
      const srcList = Array.from(tasks[src] || []);
      const dstList = Array.from(tasks[dst] || []);
      const [movedTask] = srcList.splice(source.index, 1);

      if (!movedTask) {
        console.error('❌ movedTask غير موجود عند index:', source.index);
        return;
      }

      movedTask.etape = dst;

      dstList.splice(destination.index, 0, movedTask);

      // Marquer le ticket comme en cours de mise à jour
      setPendingUpdates(prev => new Set([...prev, movedTask.id]));

      // Mettre à jour l'interface immédiatement
      setTasks(prev => ({ ...prev, [src]: srcList, [dst]: dstList }));

      try {
        const previousEtapeNom = sourceStage?.nom || 'Inconnue';
        const currentEtapeNom = destStage?.nom || 'Inconnue';
        const modifier = utilisateur_id;

        console.log('Données à envoyer:', {
          ticketId: movedTask.id,
          etape_id: dst,
          previous_etape_nom: previousEtapeNom,
          current_etape_nom: currentEtapeNom,
          modifier: parseInt(modifier, 10),
          testeur_id: testeur_id ? parseInt(testeur_id, 10) : null,
        });

        if (!movedTask.id || !dst) {
          throw new Error('Données manquantes: ticket ID ou étape destination');
        }

        // Préparer les données à envoyer
        const updateData = {
          etape_id: dst,
          status: 1,
          modifier: parseInt(modifier, 10),
        };

        // Ajouter testeur_id si disponible
        if (testeur_id) {
          updateData.testeur_id = parseInt(testeur_id, 10);
        }

        const response = await api.post(`/tickets/${movedTask.id}`, updateData);

        console.log('✅ Réponse API:', response.data);
        console.log('✅ Ticket mis à jour avec succès');

        // Retirer le ticket de la liste des mises à jour en cours
        setPendingUpdates(prev => {
          const newSet = new Set(prev);
          newSet.delete(movedTask.id);
          return newSet;
        });

      } catch (err) {
        console.error('Erreur complète:', err);
        console.error('Détails de la réponse:', err.response?.data);
        console.error('Statut:', err.response?.status);

        // Retirer de la liste des mises à jour en cours même en cas d'erreur
        setPendingUpdates(prev => {
          const newSet = new Set(prev);
          newSet.delete(movedTask.id);
          return newSet;
        });

        // Rollback en cas d'erreur
        const rollbackSrcList = Array.from(tasks[dst] || []);
        const rollbackDstList = Array.from(tasks[src] || []);
        const [rollbackMoved] = rollbackSrcList.splice(destination.index, 1);
        rollbackMoved.etape = src;
        rollbackDstList.splice(source.index, 0, rollbackMoved);
        setTasks(prev => ({ ...prev, [src]: rollbackDstList, [dst]: rollbackSrcList }));
      }

      closeConfirmationModal();
    };

    // Vérifier si une confirmation est nécessaire
    if (needsConfirmation(src, dst)) {
      const modalType = getModalType(dst);
      let confirmMessage = '';
      let confirmTitle = '';

      if (destStage?.nom?.toUpperCase().includes('LIVRÉE')) {
        confirmTitle = 'Confirmer la livraison';
        confirmMessage = `Voulez-vous vraiment marquer le ticket "${taskTitle}" comme livré ? Cette action indiquera que le développement est terminé et le ticket sera prêt pour la validation client.`;
      } else if (destStage?.nom?.toUpperCase().includes('TERMINÉ')) {
        confirmTitle = 'Confirmer la finalisation';
        confirmMessage = `Voulez-vous vraiment marquer le ticket "${taskTitle}" comme terminé ? Cette action finalisera définitivement le ticket.`;
      } else if (destStage?.nom?.toUpperCase().includes('VALIDÉ')) {
        confirmTitle = 'Confirmer la validation';
        confirmMessage = `Voulez-vous vraiment valider le ticket "${taskTitle}" ? Cette action confirmera que le ticket répond aux exigences.`;
      } else {
        confirmTitle = 'Confirmer le déplacement';
        confirmMessage = `Voulez-vous vraiment déplacer le ticket "${taskTitle}" vers l'étape "${destStage?.nom}" ? Cette action changera le statut du ticket.`;
      }

      setConfirmationModal({
        isOpen: true,
        title: confirmTitle,
        message: confirmMessage,
        onConfirm: performMove,
        type: modalType
      });
    } else {
      // Pour les déplacements normaux, exécuter directement
      performMove();
    }
  };

  // 6) Filtrage recherche et dates
  const filteredTasks = permittedStages.reduce((acc, stage) => {
    acc[stage.id] = (tasks[stage.id] || []).filter(task => {
      // Filtre texte (inchangé)
      // const textMatch = !searchTerm || [ /* ... */ ]
      //   .some(field => field && field.toString().toLowerCase().includes(searchTerm.toLowerCase()));
      const textMatch = !searchTerm ||
        [task.title, task.content, task.module?.nom, task.organisation?.nomOrganisation, task.type]
          .some(field => field && field.toString().toLowerCase().includes(searchTerm.toLowerCase()));


      // Filtre plage de dates client (inchangé)
      const deadlineDate = task.deadlineClient
        ? new Date(`${task.deadlineClient}T00:00:00`)
        : null;
      let dateMatch = true;
      if (dateStart) {
        const start = new Date(`${dateStart}T00:00:00`);
        dateMatch = dateMatch && (!!deadlineDate && deadlineDate >= start);
      }
      if (dateEnd) {
        const end = new Date(`${dateEnd}T23:59:59`);
        dateMatch = dateMatch && (!!deadlineDate && deadlineDate <= end);
      }

      // Filtre priorité / type (inchangé)
      const priorityMatch = !priorityFilter || task.priorite === priorityFilter;
      const typeMatch = !typeFilter || task.type === typeFilter;

      // Filtre module / organisation (usage de .id)
      const moduleMatch = !moduleFilter || Number(task.module) === Number(moduleFilter);
      const organisationMatch = !organisationFilter || (task.organisation?.id == organisationFilter);

      // Filtre échéance technique
      const techMatch = !deadlineTechFilter ||
        task.deadlineTech === deadlineTechFilter;

      // Filtre échéance client (exact match si rempli)
      const clientMatch = !deadlineClientFilter ||
        task.deadlineClient === deadlineClientFilter;

      return textMatch
        && dateMatch
        && priorityMatch
        && typeMatch
        && moduleMatch
        && organisationMatch
        && techMatch
        && clientMatch;
    });
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <img src={progress2} alt="Loading" className="w-24 h-24" />
      </div>
    );
  }

  return (
    <>
      {isProcessing && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[60]">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="bg-white/90 rounded-xl p-8 shadow-lg border border-terrecuite-100 max-w-sm w-full mx-4"
          >
            <div className="flex flex-col items-center text-center space-y-5">
              {/* Logo animé avec conteneur ajusté */}
              <motion.div
                className="w-20 h-20 mb-3"
                animate={{
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  scale: {
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }
                }}
              >
                <img
                  src={logo}
                  alt="Chargement en cours"
                  className="w-full h-full object-contain"
                />
              </motion.div>

              {/* Texte avec hiérarchie visuelle améliorée */}
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-terrecuite-700">
                  Traitement en cours...
                </h3>
                <p className="text-terrecuite-500/90 text-sm leading-relaxed">
                  Nous traitons votre demande, merci de patienter.
                </p>
              </div>

              {/* Barre de progression subtile avec plus de transparence */}
              <div className="w-full bg-terrecuite-100/50 rounded-full h-1.5 mt-2">
                <motion.div
                  className="bg-terrecuite-500/80 h-full rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: ["0%", "70%", "100%", "0%"] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <div className="flex items-center mb-4">
              <span className="text-red-500 text-xl mr-3">⚠</span>
              <h3 className="text-lg font-semibold text-gray-800">Retour Fonctionnel</h3>
            </div>
            <p className="text-gray-600 mb-3">
              Veuillez saisir une raison pour retourner le ticket <strong>"{pendingMove?.ticket?.title}"</strong>.
            </p>

            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
              rows={4}
              placeholder="Raison du retour..."
              value={causeText}
              onChange={(e) => setCauseText(e.target.value)}
              disabled={isProcessing}
            />

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowReturnModal(false); setCauseText(''); }}
                disabled={isProcessing}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                disabled={!causeText.trim() || isProcessing}
                onClick={handleConfirmReject}
                className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 ${causeText.trim() && !isProcessing
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-300 cursor-not-allowed"
                  }`}
              >
                {isProcessing ? (
                  <>
                    <motion.div
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Traitement...
                  </>
                ) : (
                  "Retourner"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}


      {/* Modal de Confirmation */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onCancel={closeConfirmationModal}
        type={confirmationModal.type}
        confirmText="Confirmer"
        cancelText="Annuler"
      />

     <DragDropContext onDragEnd={onDragEnd}>
        <div className={`p-4 overflow-x-auto min-h-[calc(100vh-64px)] ${isMobile ? 'flex flex-col gap-3' : 'flex gap-4'}`}>
          {permittedStages.map(stage => (
            <TaskColumn
              key={stage.id}
              droppableId={String(stage.id)}
              title={stage.nom}
              count={filteredTasks[stage.id]?.length || 0}
              tasks={filteredTasks[stage.id] || []}
              couleur={stage.couleur}
              isMobile={isMobile}
            />
          ))}
        </div>
      </DragDropContext>
    </>
  );
};

export default TaskBoard;